/* === UPDATED FILE: src/pages/courses/CoursePlayerPage.js === */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Header from '../../components/Header';
import Navbar from '../../components/Navbar';
import TestPlayer from './TestPlayer';

const getAuthInfo = () => {
    const token = localStorage.getItem('app_token');
    if (!token) return { isLoggedIn: false, userId: null, token: null };
    try {
        const decoded = jwtDecode(token);
        return { isLoggedIn: true, userId: decoded.googleId, token };
    } catch (error) {
        return { isLoggedIn: false, userId: null, token: null };
    }
};

const CoursePlayerPage = () => {
    const { courseId } = useParams();
    const [course, setCourse] = useState(null);
    const [activeLecture, setActiveLecture] = useState(null);
    const [completedLectures, setCompletedLectures] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCourseData = useCallback(async () => {
        setIsLoading(true);
        const { isLoggedIn, token } = getAuthInfo();
        try {
            const courseRes = await axios.get(`/api/courses/${courseId}`);
            if (!courseRes.data) throw new Error('Course not found.');
            
            const courseData = courseRes.data;
            setCourse(courseData);

            if (isLoggedIn && courseData.course_id) {
                const progressRes = await axios.get(`/api/courses/${courseData.course_id}/progress`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCompletedLectures(new Set(progressRes.data));
            }

            if (courseData.lectures && courseData.lectures.length > 0) {
                setActiveLecture(courseData.lectures[0]);
            }
        } catch (err) {
            setError('Failed to load course content.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [courseId]);

    useEffect(() => {
        fetchCourseData();
    }, [fetchCourseData]);

    const handleGoToNextLecture = useCallback(() => {
        if (!course || !activeLecture) return;
    
        const currentIndex = course.lectures.findIndex(lec => lec.id === activeLecture.id);
    
        // Check if it's not the last lecture
        if (currentIndex !== -1 && currentIndex < course.lectures.length - 1) {
            const nextLecture = course.lectures[currentIndex + 1];
            setActiveLecture(nextLecture);
        }
    }, [course, activeLecture]);

    const markLectureAsComplete = useCallback(async (lectureToComplete) => {
        if (!course?.course_id || !lectureToComplete?.lecture_id || completedLectures.has(lectureToComplete.lecture_id)) {
            // If already complete, just try to advance
            if (completedLectures.has(lectureToComplete.lecture_id)) {
                handleGoToNextLecture();
            }
            return;
        }

        const { isLoggedIn, token } = getAuthInfo();
        if (!isLoggedIn) return;

        try {
            await axios.post('/api/courses/progress', 
                { lectureId: lectureToComplete.lecture_id, courseId: course.course_id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Optimistically update the UI
            setCompletedLectures(prev => new Set(prev).add(lectureToComplete.lecture_id));
            // Automatically go to the next lecture
            handleGoToNextLecture();
        } catch (err) {
            console.error("Failed to save progress:", err);
            // If it fails, you might want to revert the optimistic update or show an error
        }
    }, [completedLectures, course, handleGoToNextLecture]);

    useEffect(() => {
        if (course && activeLecture && activeLecture.lecture_type === 'text') {
            markLectureAsComplete(activeLecture);
        }
    }, [course, activeLecture, markLectureAsComplete]);

    const isLectureUnlocked = (lecture, index) => {
        if (index === 0) return true;
        const previousLecture = course.lectures[index - 1];
        return completedLectures.has(previousLecture.lecture_id);
    };

    const getIcon = (lecture, isUnlocked) => {
        if (!isUnlocked) return 'lock';
        if (completedLectures.has(lecture.lecture_id)) return 'check_circle';
        
        switch (lecture.lecture_type) {
            case 'video': return 'play_circle';
            case 'text': return 'article';
            case 'test': return 'quiz';
            default: return 'help';
        }
    };
    
    const getSafeS3Url = (url) => {
        if (!url || !url.includes('s3.ap-south-1.amazonaws.com')) return url;
        const match = url.match(/https:\/\/(.*?)\.s3\.ap-south-1\.amazonaws\.com\/(.*)/);
        if (match && match[1] && match[2]) {
            const bucket = match[1];
            const key = match[2];
            return `https://s3.ap-south-1.amazonaws.com/${bucket}/${key}`;
        }
        return url;
    };

    const renderMainContent = () => {
        if (!activeLecture) return <div className="video-player-mock"><p>Select a lecture to begin.</p></div>;

        switch (activeLecture.lecture_type) {
            case 'video':
                const videoUrl = getSafeS3Url(activeLecture.content);
                return (
                    <div className="video-player-wrapper">
                        <video 
                            key={activeLecture.id} 
                            src={videoUrl} 
                            className="video-element" 
                            controls 
                            autoPlay
                            onEnded={() => markLectureAsComplete(activeLecture)}
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>
                );
            case 'text':
                return (
                    <div className="article-text-content"
                         style={{ padding: '1.5rem', background: 'var(--admin-sidebar-bg)', borderRadius: '12px' }}
                         dangerouslySetInnerHTML={{ __html: activeLecture.content }} 
                    />
                );
            case 'test':
                return (
                    <TestPlayer 
                        lectureId={activeLecture.id} 
                        onTestComplete={(passed) => {
                            if (passed) {
                                // The markLectureAsComplete function will now handle moving to the next lecture
                                markLectureAsComplete(activeLecture);
                            }
                        }}
                    />
                );
            default:
                return <p>Unsupported lecture type.</p>;
        }
    };

    const progressPercentage = course?.lectures?.length > 0
        ? (completedLectures.size / course.lectures.length) * 100
        : 0;
        
    const activeLectureIndex = course?.lectures?.findIndex(lec => lec.id === activeLecture?.id) ?? -1;
    const isLastLecture = activeLectureIndex === -1 || activeLectureIndex === (course?.lectures.length - 1);

    if (isLoading) return <div className="app-container"><Header title="Loading Course..." showBackButton={true} /><main className="page-padding"><p>Loading...</p></main><Navbar /></div>;
    if (error) return <div className="app-container"><Header title="Error" showBackButton={true} /><main className="page-padding"><p className="error-message">{error}</p></main><Navbar /></div>;

    return (
        <div className="app-container">
            <Header title={course?.title || "Course"} showBackButton={true} />
            <main className="page-padding">
                <div className="course-player-layout">
                    <div className="course-main-content">
                        <div className="progress-bar-container" style={{ marginBottom: '1.5rem' }}>
                            <div className="progress-bar-background" style={{ background: '#e2e8f0', borderRadius: '8px', padding: '2px' }}>
                                <div 
                                    className="progress-bar-foreground"
                                    style={{
                                        width: `${progressPercentage}%`,
                                        height: '12px',
                                        backgroundColor: '#16a085',
                                        borderRadius: '6px',
                                        transition: 'width 0.5s ease-in-out'
                                    }}
                                />
                            </div>
                            <p style={{ textAlign: 'right', fontSize: '0.8rem', color: '#7f8c8d', margin: '4px 0 0' }}>
                                {completedLectures.size} of {course?.lectures?.length || 0} lessons completed
                            </p>
                        </div>

                        <h2 className="video-title" style={{ marginBottom: '1rem' }}>
                            {activeLecture?.title || 'Select a Lecture'}
                        </h2>

                        {renderMainContent()}

                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={handleGoToNextLecture}
                                disabled={isLastLecture || !isLectureUnlocked(course?.lectures[activeLectureIndex + 1], activeLectureIndex + 1)}
                                className="button-primary"
                                style={{
                                    cursor: (isLastLecture || !isLectureUnlocked(course?.lectures[activeLectureIndex + 1], activeLectureIndex + 1)) ? 'not-allowed' : 'pointer',
                                    opacity: (isLastLecture || !isLectureUnlocked(course?.lectures[activeLectureIndex + 1], activeLectureIndex + 1)) ? 0.6 : 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                Next Lecture
                                <span className="material-icons">arrow_forward</span>
                            </button>
                        </div>
                    </div>

                    <aside className="course-syllabus-sidebar">
                        <h3 className="sidebar-title">{course?.title}</h3>
                        <ul className="syllabus-list">
                            {course?.lectures?.map((lecture, index) => {
                                const isUnlocked = isLectureUnlocked(lecture, index);
                                return (
                                <li 
                                    key={lecture.id} 
                                    className={`syllabus-item ${activeLecture?.id === lecture.id ? 'active' : ''} ${!isUnlocked ? 'locked' : ''}`}
                                    onClick={() => isUnlocked && setActiveLecture(lecture)}
                                    style={{
                                        backgroundColor: activeLecture?.id === lecture.id ? '#f1f5f9' : 'transparent',
                                        fontWeight: activeLecture?.id === lecture.id ? '700' : '500'
                                    }}
                                >
                                    <span className="item-icon material-icons" style={{color: completedLectures.has(lecture.lecture_id) ? '#27ae60' : '#16a085'}}>
                                        {getIcon(lecture, isUnlocked)}
                                    </span>
                                    <div className="item-details">
                                        <span className="item-title">{lecture.title}</span>
                                        <span className="item-duration">{lecture.lecture_type}</span>
                                    </div>
                                </li>
                            )})}
                        </ul>
                    </aside>
                </div>
            </main>
            <Navbar />
        </div>
    );
};

export default CoursePlayerPage;
